'use client'

import groupBy from 'lodash/groupBy'
import keys from 'lodash/keys'
import React, {useEffect, useRef, useState} from 'react'

import {mediaSizes} from '@/src/config/theme'

import {Shop} from '@/src/entities/shop'

import {FormTemplate} from '@/src/shared/modules/form'

import {ShopMapBlockLazy} from '@/src/features/common/yandex-map/ui/shop-map-block-lazy'

import {Collapse} from '@/src/shared/ui/collapse'
import {headerHeight} from '@/src/shared/utils/functions'

import * as UI from './shop-map-list.styled'

export interface ShopMapListProps {
  shopId?: Id
  shops: Shop[]
}

export const ShopMapList = ({shopId: shopIdDefault, shops}: ShopMapListProps) => {
  const mapRef = useRef<Nullable<HTMLDivElement>>(null)

  const [shopId, setId] = useState(shopIdDefault)
  const [mapRequested, setMapRequested] = useState(false)

  useEffect(() => {
    if (mapRequested || !mapRef.current || typeof IntersectionObserver === 'undefined') return

    const observer = new IntersectionObserver(
      entries => {
        if (entries.some(entry => entry.isIntersecting)) {
          setMapRequested(true)
          observer.disconnect()
        }
      },
      {rootMargin: '300px'},
    )

    observer.observe(mapRef.current)
    return () => observer.disconnect()
  }, [mapRequested])

  const scrollToMap = (mapNode: HTMLElement) => {
    const el = mapNode

    try {
      window.scroll({
        top: el.offsetTop - headerHeight(),
        behavior: 'smooth',
      })
    } catch (e) {
      console.error(e)
      el!.scrollIntoView()
    }
  }

  const onSelect = (shop: Shop) => {
    setId(shop.id)
    setMapRequested(true)

    if (mapRef.current) {
      scrollToMap(mapRef.current)
    }
  }

  const shopsByCity = groupBy(shops, shop => shop.properties?.city || '')

  return (
    <UI.FormTemplate as={FormTemplate} title="Наши адреса и контакты:">
      <UI.Container>
        <UI.ShopList>
          {keys(shopsByCity).map(city => (
            <React.Fragment key={city}>
              {city && <UI.City>{city}:</UI.City>}

              {shopsByCity[city]!.map(shop => (
                <ShopItem
                  key={shop.id}
                  onSelect={() => onSelect(shop)}
                  shop={shop}
                  active={shop.id === shopId}
                />
              ))}

              <UI.CollapseByCity
                as={Collapse}
                items={[
                  {
                    label: city || 'Другие города',
                    key: city || 'other-cities-collapse',
                    children: (
                      <UI.CollapseShopList
                        as={Collapse}
                        items={shopsByCity[city]!.map(shop => ({
                          label: shop.title,
                          id: shop.id,
                          key: shop.id,
                          children: (
                            <ShopItem
                              collapse
                              key={shop.id}
                              onSelect={() => onSelect(shop)}
                              shop={shop}
                              active={shop.id === shopId}
                            />
                          ),
                        }))}
                      />
                    ),
                  },
                ]}
              />
            </React.Fragment>
          ))}
        </UI.ShopList>
      </UI.Container>

      <UI.Map ref={mapRef}>
        {mapRequested && <ShopMapBlockLazy shops={shops} shopId={shopId} onSelect={onSelect} />}
      </UI.Map>
    </UI.FormTemplate>
  )
}

interface IShopItemProps {
  shop: Shop
  onSelect: () => void
  active: boolean
  collapse?: boolean
}

const ShopItem = ({onSelect, shop, active, collapse}: IShopItemProps) => {
  return (
    <UI.ShopItem
      onClick={() => {
        if (window.outerWidth >= mediaSizes.md) {
          onSelect()
        }
      }}
      $active={active}
      $collapse={collapse}
      role="row"
      aria-label="shop-item">
      <UI.ItemRadio type="radio" checked={active} />

      <UI.ShopItemName>{shop.title}</UI.ShopItemName>
      <UI.ShopItemAddress>
        <span data-address="">{shop.address}</span>
      </UI.ShopItemAddress>
      <UI.ShopItemSchedule>
        <span data-schedule="">{shop.work_schedule || '—'}</span>
      </UI.ShopItemSchedule>

      <UI.SeeButton outlined primary onClick={() => onSelect()}>
        Посмотреть на карте
      </UI.SeeButton>
    </UI.ShopItem>
  )
}
